(function () {
  'use strict';

  var AUTO_ADVANCE_DELAY = 350;
  var STATE_VERSION = 2;
  // How long the questions area (steps + progress bar/shell) fades out before the results
  // panel fades in — see showResults(). Kept in sync with the CSS transition duration on
  // .quiz-fade-invisible/.quiz-fade-in in component-product-quiz.css.
  var RESULTS_TRANSITION_MS = 260;

  // Add-to-cart modal: how many times a single line retries after a network
  // failure (fetch itself throwing — not a real Shopify rejection like
  // out-of-stock, which retrying can never fix), how long to wait between
  // attempts, and the rotating "still working" copy shown meanwhile so the
  // wait doesn't feel stalled.
  var CART_ADD_MAX_ATTEMPTS = 3;
  var CART_ADD_RETRY_DELAY_MS = 900;
  var CART_WAITING_MESSAGES = [
    'Adding your bundle…',
    'Locking in your price…',
    'Grabbing your free gifts…'
  ];
  var CART_WAITING_ROTATE_MS = 2000;

  function parseTags(raw) {
    var tags = {};
    if (!raw) return tags;
    raw.split(',').forEach(function (part) {
      part = part.trim();
      if (!part) return;
      var pieces = part.split(':');
      var tag = pieces[0].trim();
      var weight = parseFloat(pieces[1]);
      if (!tag) return;
      tags[tag] = (tags[tag] || 0) + (isNaN(weight) ? 1 : weight);
    });
    return tags;
  }

  function ProductQuiz(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id') || '';
    this.persist = root.getAttribute('data-persist') === 'true';
    this.fallbackTag = root.getAttribute('data-fallback-tag') || '';
    this.moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    this.storageKey = 'tru_quiz_state_' + this.sectionId;

    this.intro = root.querySelector('[data-quiz-intro]');
    this.stepsContainer = root.querySelector('[data-quiz-steps]');
    // Full, unfiltered list — every <Question> block, branch questions
    // included. Kept around so answers can stay keyed by a stable index
    // regardless of which branch ends up active (see getActiveSteps).
    this.steps = this.stepsContainer
      ? Array.prototype.slice.call(this.stepsContainer.querySelectorAll('[data-quiz-step]'))
      : [];
    this.resultsPanel = root.querySelector('[data-quiz-results]');
    this.results = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-result]'));
    this.progress = root.querySelector('[data-quiz-progress]');
    // .product-quiz__progress--none is a CSS-level display:none — see that class's own
    // comment in component-product-quiz.css for why this can't just be the `hidden`
    // attribute (begin()/restore() clear `hidden` unconditionally once the quiz starts).
    this.progressEnabled = !!this.progress && !this.progress.classList.contains('product-quiz__progress--none');
    // Plural on purpose: the hero-step layout (quiz-step-hero.liquid) renders its own fill bar
    // + "Question X of Y" counter inside EVERY step (right above that step's own heading,
    // matching Figma) instead of once globally like the legacy progress_style options —
    // updateProgress() below writes the same value into every match it finds, so this still
    // works fine when there's only one (the legacy case). Only the currently-visible step's
    // copy is ever actually seen, since the rest are hidden via step.hidden.
    this.progressFill = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-progress-fill]'));
    this.progressDots = this.progress ? Array.prototype.slice.call(this.progress.querySelectorAll('[data-quiz-dot]')) : [];
    this.progressCurrent = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-current]'));
    this.progressTotal = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-total]'));
    // Steps in each branch are always the same length by design (see the header comment at
    // the top of sections/product-quiz.liquid) — used as the "of N" denominator before Q1 is
    // answered, when getActiveSteps() can only see the still-unresolved branch-less step
    // itself (activeSteps.length === 1 at that point, which would otherwise show "1 of 1").
    this.totalStepsEstimate = this.estimateTotalSteps();
    this.emailInput = root.querySelector('[data-quiz-email-input]');
    this.emailError = root.querySelector('[data-quiz-email-error]');
    this.bundleLink = root.querySelector('[data-quiz-bundle-link]');
    this.bundleLinkLabel = root.querySelector('[data-quiz-bundle-link-label]');

    this.index = 0;
    this.answers = {};
    this.email = '';
    this.emailCaptured = false;
    // Which of the 3 result branches (set by Q1's answer) gates which
    // follow-up steps are part of the active sequence — see
    // recordChoiceAnswer/getActiveSteps.
    this.selectedBranch = null;
    this.activeSteps = [];

    this.bindEvents();
    this.restore();
    this.bindResize();

    var self = this;
    window.requestAnimationFrame(function () {
      self.equalizeStepHeights();
    });
  }

  // No-branch step count + the largest single branch's step count — see
  // this.totalStepsEstimate above for why this exists.
  ProductQuiz.prototype.estimateTotalSteps = function () {
    var noBranchCount = 0;
    var perBranch = {};
    this.steps.forEach(function (step) {
      var branch = step.getAttribute('data-branch');
      if (!branch) {
        noBranchCount++;
      } else {
        perBranch[branch] = (perBranch[branch] || 0) + 1;
      }
    });
    var maxBranch = 0;
    Object.keys(perBranch).forEach(function (key) {
      if (perBranch[key] > maxBranch) maxBranch = perBranch[key];
    });
    return noBranchCount + maxBranch;
  };

  // The first question with no data-branch is the one whose answer decides
  // the branch — every step tagged data-branch="X" only appears in the
  // active sequence once that question resolves to tag "X". Steps with no
  // data-branch (just the first question, in practice) always show.
  ProductQuiz.prototype.getActiveSteps = function () {
    var branch = this.selectedBranch;
    return this.steps.filter(function (step) {
      var stepBranch = step.getAttribute('data-branch');
      return !stepBranch || stepBranch === branch;
    });
  };

  ProductQuiz.prototype.bindEvents = function () {
    var self = this;

    var startButton = this.root.querySelector('[data-quiz-start]');
    if (startButton) {
      startButton.addEventListener('click', function () {
        self.begin();
      });
    }

    if (this.emailInput) {
      this.emailInput.addEventListener('input', function () {
        if (self.emailError) self.emailError.hidden = true;
        self.emailInput.removeAttribute('aria-invalid');
      });
    }

    if (this.bundleLink) {
      this.bundleLink.addEventListener('click', function (event) {
        self.handleBundleLinkClick(event);
      });
    }

    Array.prototype.slice.call(this.root.querySelectorAll('[data-quiz-retake]')).forEach(function (retakeButton) {
      retakeButton.addEventListener('click', function () {
        self.retake();
      });
    });

    this.steps.forEach(function (step) {
      var optionsWrap = step.querySelector('[data-quiz-options]');
      if (optionsWrap) {
        optionsWrap.addEventListener('click', function (event) {
          var option = event.target.closest('[data-quiz-option]');
          if (!option) return;
          self.handleOptionClick(step, option);
        });
      }

      var scale = step.querySelector('[data-quiz-scale], [data-quiz-number]');
      if (scale) {
        scale.addEventListener('input', function () {
          self.handleScaleInput(step, scale);
        });
      }

      var nextButton = step.querySelector('[data-quiz-next]');
      if (nextButton) {
        nextButton.addEventListener('click', function () {
          self.next();
        });
      }

      var backButton = step.querySelector('[data-quiz-back]');
      if (backButton) {
        backButton.addEventListener('click', function () {
          self.back();
        });
      }
    });

    this.results.forEach(function (resultEl) {
      Array.prototype.slice.call(resultEl.querySelectorAll('[data-quiz-item]')).forEach(function (item) {
        self.bindComponentSelects(item);
        self.bindQtyStepper(item);
        self.bindSelectRemove(item, resultEl);
        self.bindPurchaseOptions(item);
      });

      self.bindGiftModals(resultEl);
      self.bindCartModal(resultEl);
      self.bindImageCarousel(resultEl);
      self.bindFlavorDropdowns(resultEl);
      self.syncStockAvailability(resultEl);

      var orderSummaryAdd = resultEl.querySelector('[data-order-summary-add]');
      if (orderSummaryAdd) {
        orderSummaryAdd.addEventListener('click', function () {
          var featuredItem = resultEl.querySelector('[data-quiz-item]');
          var statusEl = resultEl.querySelector('[data-order-summary-status]');
          if (featuredItem) self.addBundleToCart(featuredItem, orderSummaryAdd, statusEl);
        });
      }
    });

    // Once per quiz instance, not once per result — closeAllFlavorDropdowns is already
    // document-wide, no need to re-bind these for every result.
    document.addEventListener('click', function (event) {
      if (!event.target.closest('[data-quiz-flavor-wrap]')) self.closeAllFlavorDropdowns();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') self.closeAllFlavorDropdowns();
    });
  };

  // Each flavored component (Protein Complete always, Energy Complete for
  // Muscle Mami too) gets its own independent <select> built from that
  // product's real variants — never the packaged bundle SKU's variants,
  // whose option values don't reliably name-match the standalone products'
  // (e.g. the bundle's "Vanilla Cream" vs Protein Complete's "Natural
  // Vanilla Cream", or "Cinnamon Bun" existing on the bundle but not as a
  // standalone flavor at all). Changing a select just re-renders this
  // component's line in the Order Summary; it doesn't touch cart contents
  // until Add to Cart is clicked.
  ProductQuiz.prototype.bindComponentSelects = function (item) {
    var self = this;
    Array.prototype.slice.call(item.querySelectorAll('[data-component-select]')).forEach(function (select) {
      select.addEventListener('change', function () {
        var component = select.closest('[data-component]');
        var option = select.options[select.selectedIndex];

        if (component && option) {
          // Server-rendered data-available only reflects the DEFAULT variant picked at
          // render time (selected_or_first_available_variant) — once the shopper can pick a
          // different one themselves (quiz-hero__flavor-row), it has to be kept in sync here
          // too, or syncStockAvailability would keep judging stock by a flavor they're not
          // even buying anymore. (Swatch/label sync for the custom dropdown itself lives in
          // syncFlavorVisual, bound separately by bindFlavorDropdowns.)
          component.setAttribute('data-available', option.disabled ? 'false' : 'true');

          var resultEl = component.closest('[data-quiz-result]');
          if (resultEl) self.syncStockAvailability(resultEl);
        }

        self.recomputeOrderSummary(item);
      });
    });
  };

  // Custom flavor dropdown — ported from the PDP's own flavor picker
  // (.mpdp-flavor-select-wrap in sections/shop-section-reimagined-pdp.liquid): the real
  // <select data-component-select> stays in the DOM as the actual source of truth (still what
  // bindComponentSelects/getBundleCartItems read), swapped visually for a swatch+name trigger
  // (quiz-hero__flavor-visual) and a listbox built here in JS (quiz-hero__flavor-options) —
  // a native select's own OS-drawn option popup can't be styled via CSS at all, which is why
  // it looked unstyled/"crudo" before this.
  ProductQuiz.prototype.bindFlavorDropdowns = function (resultEl) {
    var self = this;
    Array.prototype.slice.call(resultEl.querySelectorAll('[data-quiz-flavor-wrap]')).forEach(function (wrap) {
      var select = wrap.querySelector('[data-component-select]');
      var trigger = wrap.querySelector('[data-quiz-flavor-trigger]');
      if (!select || !trigger) return;

      self.renderFlavorOptions(wrap, select, trigger);

      trigger.addEventListener('click', function () {
        var isOpen = wrap.classList.contains('is-open');
        self.closeAllFlavorDropdowns();
        wrap.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          wrap.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });

      select.addEventListener('change', function () {
        self.syncFlavorVisual(wrap, select);
      });
    });
  };

  ProductQuiz.prototype.syncFlavorVisual = function (wrap, select) {
    var option = select.options[select.selectedIndex];
    if (!option) return;

    var swatchEl = wrap.querySelector('[data-quiz-flavor-swatch]');
    var textEl = wrap.querySelector('[data-quiz-flavor-text]');
    var color = option.getAttribute('data-swatch-color');
    if (swatchEl && color) swatchEl.style.backgroundColor = color;
    if (textEl) textEl.textContent = option.textContent;

    Array.prototype.slice.call(wrap.querySelectorAll('[data-quiz-flavor-option]')).forEach(function (row) {
      row.classList.toggle('is-active', row.getAttribute('data-value') === select.value);
    });
  };

  ProductQuiz.prototype.renderFlavorOptions = function (wrap, select, trigger) {
    var self = this;
    var optionsEl = wrap.querySelector('[data-quiz-flavor-options]');
    if (!optionsEl) return;

    optionsEl.innerHTML = '';
    Array.prototype.slice.call(select.options).forEach(function (option) {
      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'quiz-hero__flavor-option';
      row.setAttribute('data-quiz-flavor-option', '');
      row.setAttribute('role', 'option');
      row.setAttribute('data-value', option.value);
      if (option.disabled) row.disabled = true;

      var swatchSpan = document.createElement('span');
      swatchSpan.className = 'quiz-hero__flavor-option-swatch';
      swatchSpan.style.backgroundColor = option.getAttribute('data-swatch-color') || '';

      var textSpan = document.createElement('span');
      textSpan.className = 'quiz-hero__flavor-option-text';
      textSpan.textContent = option.textContent;

      row.appendChild(swatchSpan);
      row.appendChild(textSpan);
      row.addEventListener('click', function () {
        if (option.disabled) return;
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        wrap.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      });
      optionsEl.appendChild(row);
    });

    self.syncFlavorVisual(wrap, select);
  };

  // Global on purpose (matches the PDP's closeAllFlavorDropdowns) — closes every open dropdown
  // regardless of which result it belongs to, since results/components aren't tracked by
  // instance here and there's no harm closing one that was already shut.
  ProductQuiz.prototype.closeAllFlavorDropdowns = function () {
    Array.prototype.slice.call(document.querySelectorAll('[data-quiz-flavor-wrap].is-open')).forEach(function (wrap) {
      wrap.classList.remove('is-open');
      var trigger = wrap.querySelector('[data-quiz-flavor-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  };

  // Ported from the protein-complete-lp carousel (sections/shop-section-reimagined-pdp-offer.
  // liquid's lpoffer-thumbs pattern) — index-based click-to-select thumbnails + prev/next
  // arrows that scroll the strip via scrollIntoView, wrapping at both ends. No drag/swipe
  // handling of its own (native scroll already covers touch) and no autoplay/dots.
  //
  // Video slots (data-media-type="video") are the one addition over the ported pattern: a
  // click opens quiz-hero__carousel-video-modal instead of touching the hero image, and never
  // becomes "is-active" — that state tracks which PHOTO is currently shown as the hero image,
  // and a video was never shown there. Landing on a video thumb via the prev/next arrows only
  // scrolls it into view, it does not auto-open the modal — arrows are for browsing, a direct
  // click is what plays it.
  ProductQuiz.prototype.bindImageCarousel = function (resultEl) {
    var carousel = resultEl.querySelector('[data-quiz-hero-carousel]');
    if (!carousel) return;

    var mainImg = resultEl.querySelector('[data-quiz-hero-main-img]');
    var track = carousel.querySelector('[data-quiz-hero-carousel-track]');
    var prevBtn = carousel.querySelector('[data-quiz-hero-carousel-prev]');
    var nextBtn = carousel.querySelector('[data-quiz-hero-carousel-next]');
    var thumbs = Array.prototype.slice.call(carousel.querySelectorAll('[data-quiz-hero-carousel-thumb]'));
    if (!thumbs.length) return;

    // Arrows only make sense once the strip actually can't fit every thumbnail at once — with
    // enough column width to show all of them (wide desktop info column, few thumbnails) there's
    // nothing left to scroll to, so hide them instead of leaving two dead buttons in the row.
    // Re-checked on resize since that's exactly what can flip whether it overflows.
    function updateArrowVisibility() {
      if (!track) return;
      var overflowing = track.scrollWidth > track.clientWidth + 1;
      if (prevBtn) prevBtn.hidden = !overflowing;
      if (nextBtn) nextBtn.hidden = !overflowing;
    }

    updateArrowVisibility();
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(updateArrowVisibility, 150);
    });

    var videoModal = resultEl.querySelector('[data-quiz-carousel-video-modal]');
    var videoPlayer = videoModal ? videoModal.querySelector('[data-quiz-carousel-video-modal-player]') : null;

    function openVideoModal(src) {
      if (!videoModal || !videoPlayer || !src) return;
      videoPlayer.src = src;
      videoModal.hidden = false;
      document.body.classList.add('quiz-hero-modal-open');
      videoPlayer.play().catch(function () {});
    }

    function closeVideoModal() {
      if (!videoModal || !videoPlayer) return;
      videoPlayer.pause();
      videoPlayer.removeAttribute('src');
      videoPlayer.load();
      videoModal.hidden = true;
      document.body.classList.remove('quiz-hero-modal-open');
    }

    if (videoModal) {
      Array.prototype.slice.call(videoModal.querySelectorAll('[data-quiz-carousel-video-modal-close]')).forEach(function (el) {
        el.addEventListener('click', closeVideoModal);
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !videoModal.hidden) closeVideoModal();
      });
    }

    // Shoppable video ATC: Subscribe/One-time buttons in the video popup's product panel
    // don't add to cart themselves — they set the purchase type (the exact same
    // [data-quiz-purchase-option] buttons/bindPurchaseOptions the page's own toggle uses,
    // just clicked programmatically) and then click the page's real
    // [data-order-summary-add] button, so the existing getBundleCartItems/cart-modal flow
    // runs completely unmodified. If the bundle has a flavor component, a small confirmation
    // popup collects that choice first — see quiz-hero__flavor-confirm-modal's own comment in
    // quiz-result-hero.liquid for why it relocates the real .quiz-hero__components node
    // in/out instead of duplicating the flavor selects.
    var flavorModal = resultEl.querySelector('[data-quiz-flavor-confirm-modal]');
    var flavorSlot = flavorModal ? flavorModal.querySelector('[data-quiz-flavor-confirm-slot]') : null;
    var componentsAnchor = resultEl.querySelector('[data-quiz-components-anchor]');
    var confirmAddBtn = flavorModal ? flavorModal.querySelector('[data-quiz-flavor-confirm-add]') : null;
    var pendingPurchaseType = null;

    function restoreComponents() {
      if (!componentsAnchor || !componentsAnchor.parentNode || !flavorModal) return;
      var componentsWrap = flavorModal.querySelector('.quiz-hero__components');
      if (componentsWrap) componentsAnchor.parentNode.insertBefore(componentsWrap, componentsAnchor.nextSibling);
    }

    function closeFlavorModal() {
      if (!flavorModal) return;
      restoreComponents();
      flavorModal.hidden = true;
      document.body.classList.remove('quiz-hero-modal-open');
      pendingPurchaseType = null;
    }

    function openFlavorModal() {
      if (!flavorModal || !flavorSlot || !componentsAnchor) return;
      var componentsWrap = componentsAnchor.nextElementSibling;
      if (componentsWrap && componentsWrap.classList.contains('quiz-hero__components')) {
        flavorSlot.appendChild(componentsWrap);
      }
      flavorModal.hidden = false;
      document.body.classList.add('quiz-hero-modal-open');
    }

    function applyPurchaseType(purchaseType) {
      if (!purchaseType) return;
      var option = resultEl.querySelector('[data-quiz-purchase-option][data-purchase-type="' + purchaseType + '"]');
      if (option && !option.disabled) option.click();
    }

    function hasFlavorComponents() {
      var componentsWrap = resultEl.querySelector('.quiz-hero__components');
      return !!(componentsWrap && componentsWrap.querySelector('[data-quiz-flavor-wrap]'));
    }

    function addBundleToCartNow() {
      var realAddBtn = resultEl.querySelector('[data-order-summary-add]');
      if (realAddBtn) realAddBtn.click();
    }

    if (videoModal) {
      Array.prototype.slice.call(videoModal.querySelectorAll('[data-quiz-carousel-video-atc]')).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var purchaseType = btn.getAttribute('data-purchase-type');
          closeVideoModal();
          if (hasFlavorComponents()) {
            pendingPurchaseType = purchaseType;
            openFlavorModal();
          } else {
            applyPurchaseType(purchaseType);
            addBundleToCartNow();
          }
        });
      });
    }

    if (flavorModal) {
      Array.prototype.slice.call(flavorModal.querySelectorAll('[data-quiz-flavor-confirm-modal-close]')).forEach(function (el) {
        el.addEventListener('click', closeFlavorModal);
      });
      if (confirmAddBtn) {
        confirmAddBtn.addEventListener('click', function () {
          var purchaseType = pendingPurchaseType;
          restoreComponents();
          flavorModal.hidden = true;
          document.body.classList.remove('quiz-hero-modal-open');
          pendingPurchaseType = null;
          applyPurchaseType(purchaseType);
          addBundleToCartNow();
        });
      }
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !flavorModal.hidden) closeFlavorModal();
      });
    }

    function activateThumb(thumb) {
      if (thumb.getAttribute('data-media-type') === 'video') {
        openVideoModal(thumb.getAttribute('data-video-src'));
        return;
      }

      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', t === thumb);
        t.setAttribute('aria-selected', t === thumb ? 'true' : 'false');
      });

      if (mainImg) {
        var src = thumb.getAttribute('data-image-src');
        var srcset = thumb.getAttribute('data-image-srcset');
        if (src) {
          // srcset (when present) wins over src in every browser — clearing it first is what
          // actually makes the swap visible instead of the browser silently keeping whatever
          // width it already picked from the old srcset.
          if (srcset) {
            mainImg.srcset = srcset;
          } else {
            mainImg.removeAttribute('srcset');
          }
          mainImg.src = src;
        }
      }
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        activateThumb(thumb);
      });
    });

    function goToAdjacent(dir) {
      var activeIndex = 0;
      for (var i = 0; i < thumbs.length; i++) {
        if (thumbs[i].classList.contains('is-active')) {
          activeIndex = i;
          break;
        }
      }
      var next = thumbs[(activeIndex + dir + thumbs.length) % thumbs.length];
      next.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      if (next.getAttribute('data-media-type') !== 'video') activateThumb(next);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goToAdjacent(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToAdjacent(1); });
  };

  ProductQuiz.prototype.bindQtyStepper = function (item) {
    var self = this;
    var stepper = item.querySelector('[data-quiz-qty]');
    if (!stepper) return;
    var input = stepper.querySelector('[data-quiz-qty-input]');
    if (!input) return;

    stepper.addEventListener('click', function (event) {
      var button = event.target.closest('[data-quiz-qty-minus], [data-quiz-qty-plus]');
      if (!button) return;
      var min = parseInt(input.getAttribute('min'), 10) || 0;
      var value = parseInt(input.value, 10) || 0;
      value = button.hasAttribute('data-quiz-qty-plus') ? value + 1 : Math.max(min, value - 1);
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    input.addEventListener('change', function () {
      self.recomputeOrderSummary(item);
    });
  };

  // "Selected" (disabled while qty >= 1) / "Remove" (sets qty to 0, flips
  // back to an enabled "Select" button) — the single include/exclude
  // toggle for the whole bundle recommendation. The quantity input is the
  // source of truth; Order Summary just reflects it.
  ProductQuiz.prototype.bindSelectRemove = function (item, resultEl) {
    var self = this;
    var selectRow = item.querySelector('[data-quiz-select-row]');
    if (!selectRow) return;

    var qtyInput = item.querySelector('[data-quiz-qty-input]');
    var selectBtn = item.querySelector('[data-quiz-select-btn]');

    var sync = function () {
      self.syncFeaturedSelection(item);
    };

    if (qtyInput) qtyInput.addEventListener('change', sync);

    if (selectBtn) {
      selectBtn.addEventListener('click', function () {
        if (!qtyInput || (parseInt(qtyInput.value, 10) || 0) > 0) return;
        qtyInput.value = 1;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    var removeBtn = item.querySelector('[data-quiz-remove-btn]');
    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        if (!qtyInput) return;
        qtyInput.value = 0;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    sync();
  };

  // New hero layout only — the default layout has no [data-quiz-purchase-option]
  // buttons, so this is a no-op there (querySelectorAll returns an empty list).
  // Purely a display + pricing toggle: getBundleCartItems reads the resulting
  // aria-pressed state itself when Add to Cart is actually clicked, so there is
  // no separate "confirm" step.
  ProductQuiz.prototype.bindPurchaseOptions = function (item) {
    var self = this;
    var options = Array.prototype.slice.call(item.querySelectorAll('[data-quiz-purchase-option]'));
    if (!options.length) return;

    options.forEach(function (option) {
      option.addEventListener('click', function () {
        if (option.disabled) return;
        options.forEach(function (other) {
          var isActive = other === option;
          other.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          other.classList.toggle('is-active', isActive);
        });
        self.recomputeOrderSummary(item);
        var resultEl = item.closest('[data-quiz-result]');
        if (resultEl) self.syncStockAvailability(resultEl);
      });
    });
  };

  // Hero layout only — [data-component]/[data-freebie] divs carry
  // data-available="true"/"false" from the product/variant Liquid already
  // resolved server-side (see snippets/quiz-result-hero.liquid). Disables
  // Add to Cart up front whenever something that would actually be added
  // right now is out of stock, instead of letting the shopper find out only
  // after clicking. Freebies only count when they'd actually be included
  // (see getBundleCartItems's own hasPurchaseToggle/isSubscribing logic) —
  // re-run whenever the purchase-type toggle changes, since that changes
  // which freebies are in play.
  ProductQuiz.prototype.syncStockAvailability = function (resultEl) {
    var item = resultEl.querySelector('[data-quiz-item]');
    var addBtn = resultEl.querySelector('[data-order-summary-add]');
    var noticeEl = resultEl.querySelector('[data-quiz-stock-notice]');
    if (!item || !addBtn) return;

    var hasPurchaseToggle = !!item.querySelector('[data-quiz-purchase-option]');
    var isSubscribing = hasPurchaseToggle && this.getPurchaseType(item) === 'subscribe';
    var includeFreebies = !hasPurchaseToggle || isSubscribing;

    var selectors = includeFreebies ? '[data-component], [data-freebie]' : '[data-component]';
    var unavailable = [];
    Array.prototype.slice.call(item.querySelectorAll(selectors)).forEach(function (el) {
      if (el.getAttribute('data-available') === 'false') {
        var title = el.getAttribute('data-component-title');
        if (title && unavailable.indexOf(title) === -1) unavailable.push(title);
      }
    });

    addBtn.disabled = unavailable.length > 0;
    if (noticeEl) {
      if (unavailable.length > 0) {
        var verb = unavailable.length > 1 ? 'are' : 'is';
        noticeEl.textContent = unavailable.join(', ') + ' ' + verb + ' currently out of stock — check back soon.';
        noticeEl.hidden = false;
      } else {
        noticeEl.hidden = true;
      }
    }
  };

  // Hero layout only — gift tiles rendered with [data-gift-modal-trigger]
  // (see snippets/quiz-result-hero.liquid) open a per-tile info popup
  // scoped to this resultEl, so no global ids are needed. No-op wherever
  // no tile has modal copy (querySelectorAll returns an empty list).
  ProductQuiz.prototype.bindGiftModals = function (resultEl) {
    var modals = Array.prototype.slice.call(resultEl.querySelectorAll('[data-gift-modal]'));
    if (!modals.length) return;

    var closeModal = function (modal) {
      modal.hidden = true;
      document.body.classList.remove('quiz-hero-modal-open');
    };

    var openModal = function (modal) {
      modals.forEach(function (other) {
        if (other !== modal) other.hidden = true;
      });
      modal.hidden = false;
      document.body.classList.add('quiz-hero-modal-open');
    };

    modals.forEach(function (modal) {
      var index = modal.getAttribute('data-gift-modal-index');
      var trigger = resultEl.querySelector('[data-gift-modal-trigger][data-gift-modal-index="' + index + '"]');
      if (trigger) {
        trigger.addEventListener('click', function () {
          openModal(modal);
        });
      }

      Array.prototype.slice.call(modal.querySelectorAll('[data-gift-modal-close]')).forEach(function (closeEl) {
        closeEl.addEventListener('click', function () {
          closeModal(modal);
        });
      });

      // "Take This Deal" claims the freebie by switching this result to
      // Subscribe, since freebies are subscribe-gated (see getBundleCartItems)
      // — a no-op if this product has no subscribe option at all.
      var cta = modal.querySelector('[data-gift-modal-cta]');
      if (cta) {
        cta.addEventListener('click', function () {
          var item = resultEl.querySelector('[data-quiz-item]');
          var subscribeOption = item && item.querySelector('[data-quiz-purchase-option][data-purchase-type="subscribe"]');
          if (subscribeOption && !subscribeOption.disabled && subscribeOption.getAttribute('aria-pressed') !== 'true') {
            subscribeOption.click();
          }
          closeModal(modal);
        });
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openModalEl = modals.filter(function (modal) {
        return !modal.hidden;
      })[0];
      if (openModalEl) closeModal(openModalEl);
    });
  };

  ProductQuiz.prototype.syncFeaturedSelection = function (item) {
    var qtyInput = item.querySelector('[data-quiz-qty-input]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;
    var selectBtn = item.querySelector('[data-quiz-select-btn]');
    var selectBtnLabel = item.querySelector('[data-quiz-select-btn-label]');
    var removeBtn = item.querySelector('[data-quiz-remove-btn]');

    var selected = qty > 0;

    if (selectBtn) selectBtn.disabled = selected;
    if (selectBtnLabel) selectBtnLabel.textContent = selected ? 'Selected' : 'Select';
    if (removeBtn) removeBtn.hidden = !selected;
    item.classList.toggle('is-removed', !selected);

    this.recomputeOrderSummary(item);
  };

  ProductQuiz.prototype.formatMoney = function (cents) {
    var amount = (cents / 100).toFixed(2);
    var noDecimals = Math.round(cents / 100).toString();
    return (this.moneyFormat || '${{amount}}')
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', noDecimals)
      .replace('{{amount}}', amount);
  };

  // "subscribe" only when a purchase-option toggle is present AND set to it
  // (see bindPurchaseOptions) — the default layout has no such toggle at
  // all, so this always resolves to "onetime" there, unchanged from before
  // this method existed.
  ProductQuiz.prototype.getPurchaseType = function (item) {
    var active = item.querySelector('[data-quiz-purchase-option][aria-pressed="true"]');
    return active ? active.getAttribute('data-purchase-type') : 'onetime';
  };

  // Reads each component's currently-selected variant (from its <select> if
  // flavored, or its single fixed variant otherwise), updates the Order
  // Summary's per-line label + the Total, and toggles the empty/disabled
  // states when qty is 0. Also drives the new hero layout's single price
  // display (data-quiz-add-total-price) the same way — that layout has no
  // [data-quiz-order-summary] card at all, so the block below simply skips.
  ProductQuiz.prototype.recomputeOrderSummary = function (item) {
    var resultEl = item.closest('[data-quiz-result]');
    if (!resultEl) return;

    var qtyInput = item.querySelector('[data-quiz-qty-input]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;
    var isSubscribing = this.getPurchaseType(item) === 'subscribe';

    var summary = resultEl.querySelector('[data-quiz-order-summary]');
    if (summary) {
      var totalEl = summary.querySelector('[data-order-summary-total]');
      var emptyEl = summary.querySelector('[data-order-summary-empty]');

      Array.prototype.slice.call(item.querySelectorAll('[data-component]')).forEach(function (component) {
        var select = component.querySelector('[data-component-select]');
        var labelEl = summary.querySelector('[data-order-summary-line="' + component.getAttribute('data-component-title') + '"] [data-order-summary-line-flavor]');
        if (select && labelEl) {
          var option = select.options[select.selectedIndex];
          labelEl.textContent = option ? '(' + option.textContent.trim() + ')' : '';
        }
      });

      if (totalEl) totalEl.hidden = qty <= 0;
      if (emptyEl) emptyEl.hidden = qty > 0;
    }

    var addBtn = resultEl.querySelector('[data-order-summary-add]');
    if (addBtn) addBtn.disabled = qty <= 0;
    if (qty <= 0) return;

    var targetAttr = isSubscribing ? 'data-target-cents-subscribe' : 'data-target-cents';
    var targetCents = parseInt(item.getAttribute(targetAttr), 10);
    if (isNaN(targetCents)) targetCents = parseInt(item.getAttribute('data-target-cents'), 10) || 0;

    var priceOut = resultEl.querySelector('[data-order-summary-total-price], [data-quiz-add-total-price]');
    if (priceOut) priceOut.textContent = this.formatMoney(targetCents * qty);
  };

  ProductQuiz.prototype.restore = function () {
    var stored = this.persist ? this.readStorage() : null;

    // A stored entry from an older STATE_VERSION was written before fields the current code
    // depends on existed (e.g. per-answer "type"/"unit" for scale/number) — replaying it would
    // silently misbehave instead of just being treated as "nothing stored."
    if (stored && stored.version !== STATE_VERSION) {
      this.clearStorage();
      stored = null;
    }

    if (stored && stored.email) {
      this.email = stored.email;
      this.emailCaptured = true;
      if (this.emailInput) this.emailInput.value = stored.email;
    }

    if (stored && stored.completed) {
      this.answers = stored.answers || {};
      this.selectedBranch = stored.selectedBranch || null;
      this.activeSteps = this.getActiveSteps();
      this.showResults(stored.resultTag, false);
      return;
    }

    if (stored && this.steps.length) {
      this.answers = stored.answers || {};
      this.selectedBranch = stored.selectedBranch || null;
      this.activeSteps = this.getActiveSteps();
      this.applyStoredSelections();
      if (this.intro) this.intro.hidden = true;
      if (this.progress) this.progress.hidden = !this.progressEnabled;
      this.showStep(Math.min(stored.index || 0, this.activeSteps.length - 1));
      return;
    }

    this.activeSteps = this.getActiveSteps();

    if (this.intro) {
      if (this.progress) this.progress.hidden = true;
      return;
    }

    if (this.activeSteps.length) {
      this.showStep(0);
    }
  };

  ProductQuiz.prototype.applyStoredSelections = function () {
    var self = this;
    this.steps.forEach(function (step, index) {
      var answer = self.answers[index];
      if (!answer) return;

      if (answer.type === 'scale' || answer.type === 'number') {
        var scale = step.querySelector('[data-quiz-scale], [data-quiz-number]');
        var output = step.querySelector('[data-quiz-scale-value]');
        if (scale) scale.value = answer.value;
        if (output) output.textContent = self.formatScaleValue(scale);
        self.setNextEnabled(step, true);
        return;
      }

      var options = Array.prototype.slice.call(step.querySelectorAll('[data-quiz-option]'));
      var selectedValues = answer.values || [];
      options.forEach(function (option) {
        var isSelected = selectedValues.indexOf(option.getAttribute('data-value')) !== -1;
        option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
      self.updateNextButtonState(step);
    });
  };

  ProductQuiz.prototype.begin = function () {
    if (this.emailInput && !this.validateEmail()) return;
    if (this.emailInput) this.captureEmail();

    if (this.intro) this.intro.hidden = true;
    if (this.progress) this.progress.hidden = !this.progressEnabled;
    this.activeSteps = this.getActiveSteps();
    this.showStep(0);
    this.dispatchStarted();
  };

  // Pure client-side check + localStorage persist — no fetch, no cart/customer write. The
  // captured email is only ever read back by assets/product-quiz-bundle-builder-klaviyo.js as
  // an optional enrichment on its own Klaviyo track() call.
  ProductQuiz.prototype.validateEmail = function () {
    var value = (this.emailInput.value || '').trim();
    var valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    if (this.emailError) this.emailError.hidden = valid;
    if (valid) {
      this.emailInput.removeAttribute('aria-invalid');
      this.email = value;
    } else {
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.focus();
    }

    return valid;
  };

  ProductQuiz.prototype.captureEmail = function () {
    if (this.emailCaptured) return;
    this.emailCaptured = true;
    this.persistState();
    this.root.dispatchEvent(new CustomEvent('product-quiz:email-captured', {
      detail: { email: this.email },
      bubbles: true
    }));
  };

  ProductQuiz.prototype.dispatchStarted = function () {
    this.root.dispatchEvent(new CustomEvent('product-quiz:started', {
      detail: { email: this.email, totalSteps: this.steps.length },
      bubbles: true
    }));
  };

  ProductQuiz.prototype.handleOptionClick = function (step, option) {
    var inputType = step.getAttribute('data-input-type');
    var index = parseInt(step.getAttribute('data-step-index'), 10);
    var options = Array.prototype.slice.call(step.querySelectorAll('[data-quiz-option]'));

    if (inputType === 'multi_choice') {
      var nowSelected = option.getAttribute('aria-checked') !== 'true';
      option.setAttribute('aria-checked', nowSelected ? 'true' : 'false');
    } else {
      options.forEach(function (other) {
        other.setAttribute('aria-checked', other === option ? 'true' : 'false');
      });
    }

    this.recordChoiceAnswer(step, index, options);
    this.updateNextButtonState(step);
    this.persistState();

    // Multi-select needs a manual "Next" — auto-advancing on the first pick would cut the
    // shopper off before they can select a second option.
    if (inputType !== 'multi_choice') {
      var self = this;
      window.setTimeout(function () {
        self.next();
      }, AUTO_ADVANCE_DELAY);
    }
  };

  ProductQuiz.prototype.recordChoiceAnswer = function (step, index, options) {
    var question = step.getAttribute('data-question') || '';
    var inputType = step.getAttribute('data-input-type') || 'choice';
    var selected = options.filter(function (option) {
      return option.getAttribute('aria-checked') === 'true';
    });

    var tags = {};
    var values = [];
    var goals = [];
    selected.forEach(function (option) {
      values.push(option.getAttribute('data-value') || option.textContent.trim());
      var optionTags = parseTags(option.getAttribute('data-tags'));
      Object.keys(optionTags).forEach(function (tag) {
        tags[tag] = (tags[tag] || 0) + optionTags[tag];
      });
      var goalAttr = (option.getAttribute('data-goal') || '').trim();
      if (goalAttr) {
        goalAttr.split(',').forEach(function (goal) {
          goal = goal.trim();
          if (goal) goals.push(goal);
        });
      }
    });

    this.answers[index] = {
      question: question,
      type: inputType,
      values: values,
      tags: tags,
      goals: goals
    };

    // Q1 (the only step with no data-branch) is what decides which of the
    // 3 follow-up tracks becomes active — its single selected option's tag
    // *is* the branch key, so no separate data-branch attribute is needed
    // on the options themselves.
    if (!step.getAttribute('data-branch')) {
      var tagKeys = Object.keys(tags);
      if (tagKeys.length) {
        this.selectedBranch = tagKeys[0];
        this.activeSteps = this.getActiveSteps();
      }
    }
  };

  ProductQuiz.prototype.handleScaleInput = function (step, scale) {
    var index = parseInt(step.getAttribute('data-step-index'), 10);
    var question = step.getAttribute('data-question') || '';
    var output = step.querySelector('[data-quiz-scale-value]');
    if (output) output.textContent = this.formatScaleValue(scale);

    this.answers[index] = {
      question: question,
      type: step.getAttribute('data-input-type'),
      value: scale.value,
      unit: scale.getAttribute('data-unit') || '',
      tags: {},
      goals: []
    };

    this.setNextEnabled(step, true);
    this.persistState();
  };

  ProductQuiz.prototype.formatScaleValue = function (scale) {
    var unit = scale.getAttribute('data-unit') || '';
    return unit ? scale.value + ' ' + unit : scale.value;
  };

  ProductQuiz.prototype.updateNextButtonState = function (step) {
    var inputType = step.getAttribute('data-input-type');

    if (inputType === 'scale' || inputType === 'number') {
      this.setNextEnabled(step, true);
      return;
    }

    var required = step.getAttribute('data-required') === 'true';
    if (!required) {
      this.setNextEnabled(step, true);
      return;
    }

    var selectedCount = step.querySelectorAll('[data-quiz-option][aria-checked="true"]').length;

    if (inputType === 'multi_choice') {
      var min = parseInt(step.getAttribute('data-min'), 10) || 0;
      var max = parseInt(step.getAttribute('data-max'), 10) || Infinity;
      this.setNextEnabled(step, selectedCount >= min && selectedCount <= max);
    } else {
      this.setNextEnabled(step, selectedCount > 0);
    }
  };

  ProductQuiz.prototype.setNextEnabled = function (step, enabled) {
    var nextButton = step.querySelector('[data-quiz-next]');
    if (nextButton) nextButton.disabled = !enabled;
  };

  ProductQuiz.prototype.showStep = function (activeIndex) {
    var self = this;
    this.index = activeIndex;
    var activeSteps = this.activeSteps;
    var currentStep = activeSteps[activeIndex];

    this.steps.forEach(function (step) {
      step.hidden = step !== currentStep;
    });

    if (currentStep) {
      var backButton = currentStep.querySelector('[data-quiz-back]');
      if (backButton) backButton.hidden = activeIndex === 0 && !self.intro;
      this.updateNextButtonState(currentStep);
      var heading = currentStep.querySelector('.product-quiz__question');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }

    this.updateProgress();
    this.persistState();
  };

  ProductQuiz.prototype.updateProgress = function () {
    if (!this.progress || !this.activeSteps.length) return;
    var current = this.index + 1;
    // Before Q1 resolves a branch, activeSteps only contains that still-unresolved step
    // itself (length 1) — this.totalStepsEstimate is what "of N" should read there instead.
    var total = this.selectedBranch ? this.activeSteps.length : this.totalStepsEstimate;

    this.progressFill.forEach(function (el) {
      el.style.width = (current / total) * 100 + '%';
    });
    if (this.progressDots.length) {
      this.progressDots.forEach(function (dot, i) {
        dot.setAttribute('data-active', i <= this.index ? 'true' : 'false');
      }, this);
    }
    this.progressCurrent.forEach(function (el) {
      el.textContent = String(current);
    });
    this.progressTotal.forEach(function (el) {
      el.textContent = String(total);
    });
  };

  ProductQuiz.prototype.next = function () {
    this.dispatchStepAnswered(this.activeSteps[this.index]);

    if (this.index + 1 >= this.activeSteps.length) {
      this.finish();
      return;
    }
    this.showStep(this.index + 1);
  };

  ProductQuiz.prototype.dispatchStepAnswered = function (stepEl) {
    if (!stepEl) return;
    var index = parseInt(stepEl.getAttribute('data-step-index'), 10);
    var answer = this.answers[index];
    if (!answer) return;

    this.root.dispatchEvent(new CustomEvent('product-quiz:step-answered', {
      detail: {
        stepIndex: index,
        totalSteps: this.activeSteps.length,
        question: answer.question,
        value: answer.value,
        values: answer.values,
        tags: answer.tags,
        email: this.email
      },
      bubbles: true
    }));
  };

  ProductQuiz.prototype.back = function () {
    if (this.index === 0) {
      if (this.intro) {
        this.activeSteps[0].hidden = true;
        this.intro.hidden = false;
        if (this.progress) this.progress.hidden = true;
      }
      return;
    }
    this.showStep(this.index - 1);
  };

  ProductQuiz.prototype.pickWinningTag = function () {
    // With every follow-up (profiling-only) question carrying no tags at
    // all, Q1's own tag is always the only one with a nonzero score — this
    // is a deliberate simplification, not a fallback: Q1 alone determines
    // the bundle, the rest is pure segmentation data (see the "Data: ..."
    // annotations on each follow-up question).
    return this.selectedBranch || this.fallbackTag;
  };

  ProductQuiz.prototype.finish = function () {
    var winningTag = this.pickWinningTag();
    this.showResults(winningTag, true);
  };

  // isNewCompletion=false is a page-load restore of an already-completed quiz — the
  // questions area was never actually shown this visit (it starts server-rendered `hidden`),
  // so fading it out first would just be a pointless delay before the results even a returning
  // visitor already expects. Only a live finish() (isNewCompletion=true) gets the transition.
  ProductQuiz.prototype.showResults = function (winningTag, isNewCompletion) {
    if (this.intro) this.intro.hidden = true;

    if (!isNewCompletion) {
      if (this.stepsContainer) this.stepsContainer.hidden = true;
      if (this.progress) this.progress.hidden = true;
      this.revealResults(winningTag, isNewCompletion);
      return;
    }

    var self = this;
    if (this.stepsContainer) this.stepsContainer.classList.add('quiz-fade-invisible');
    if (this.progress) this.progress.classList.add('quiz-fade-invisible');
    window.setTimeout(function () {
      if (self.stepsContainer) {
        self.stepsContainer.hidden = true;
        self.stepsContainer.classList.remove('quiz-fade-invisible');
      }
      if (self.progress) {
        self.progress.hidden = true;
        self.progress.classList.remove('quiz-fade-invisible');
      }
      self.revealResults(winningTag, isNewCompletion);
    }, RESULTS_TRANSITION_MS);
  };

  ProductQuiz.prototype.revealResults = function (winningTag, isNewCompletion) {
    var matched = null;
    this.results.forEach(function (resultEl) {
      var isMatch = resultEl.getAttribute('data-result-tag') === winningTag;
      resultEl.hidden = !isMatch;
      if (isMatch && !matched) matched = resultEl;
    });
    if (!matched && this.results.length) {
      matched = this.results[0];
      matched.hidden = false;
    }

    this.updateBundleLink(matched);
    this.dispatchResultShown(matched, winningTag);

    if (matched) {
      var featuredItem = matched.querySelector('[data-quiz-item]');
      if (featuredItem) this.syncFeaturedSelection(featuredItem);
    }

    if (this.resultsPanel) {
      var panel = this.resultsPanel;
      if (isNewCompletion) {
        // Start from opacity:0 (quiz-fade-invisible, reused rather than a separate "start" class),
        // reveal, then let the next frame's class removal be what the browser actually
        // transitions from — removing hidden and the class in the same tick would give it
        // nothing to animate from.
        panel.classList.add('quiz-fade-invisible');
        panel.hidden = false;
        window.requestAnimationFrame(function () {
          panel.classList.remove('quiz-fade-invisible');
        });
      } else {
        panel.hidden = false;
      }
    }

    if (isNewCompletion) {
      this.persistState(winningTag, true);
      this.dispatchComplete(matched, winningTag);
    }
  };

  ProductQuiz.prototype.dispatchResultShown = function (matchedResultEl, winningTag) {
    this.root.dispatchEvent(new CustomEvent('product-quiz:result-shown', {
      detail: { tag: winningTag, resultEl: matchedResultEl },
      bubbles: true
    }));
  };

  ProductQuiz.prototype.dispatchComplete = function (matchedResultEl, winningTag) {
    var title = matchedResultEl ? matchedResultEl.querySelector('.product-quiz__result-title') : null;
    this.root.dispatchEvent(new CustomEvent('product-quiz:complete', {
      detail: {
        tag: winningTag,
        resultTitle: title ? title.textContent.trim() : '',
        answers: this.answers,
        email: this.email
      },
      bubbles: true
    }));
  };

  // Shows/hides + relabels the ONE shared "View Full Bundle Details" link (outside the
  // results loop — see sections/product-quiz.liquid) to point at whichever result just
  // matched. Pure navigation, not an add-to-cart action — reads data-bundle-link-url/
  // data-bundle-link-label off the matched result element.
  ProductQuiz.prototype.updateBundleLink = function (matchedResultEl) {
    if (!this.bundleLink) return;

    var url = matchedResultEl ? matchedResultEl.getAttribute('data-bundle-link-url') : '';
    var label = matchedResultEl ? matchedResultEl.getAttribute('data-bundle-link-label') : '';

    if (!url) {
      this.bundleLink.hidden = true;
      return;
    }

    this.bundleLink.href = url;
    if (this.bundleLinkLabel) this.bundleLinkLabel.textContent = label || 'View Full Bundle Details';
    this.bundleLink.hidden = false;
  };

  ProductQuiz.prototype.handleBundleLinkClick = function (event) {
    var url = this.bundleLink.getAttribute('href');
    if (!url || url === '#') return;

    var matched = this.results.filter(function (resultEl) {
      return !resultEl.hidden;
    })[0];

    this.root.dispatchEvent(new CustomEvent('product-quiz:bundle-link-clicked', {
      detail: {
        url: url,
        label: this.bundleLinkLabel ? this.bundleLinkLabel.textContent.trim() : '',
        tag: matched ? matched.getAttribute('data-result-tag') : '',
        email: this.email
      },
      bubbles: true
    }));

    // Give tracking listeners (e.g. the Klaviyo bridge) a brief head start before the browser
    // navigates away — an in-flight request can otherwise get cut off by the page unload
    // before it reaches the network.
    event.preventDefault();
    window.setTimeout(function () {
      window.location.href = url;
    }, 250);
  };

  // Resolves each of this item's components (flavored via its <select>,
  // fixed via its single data-variant-id) into a /cart/add.js payload,
  // tagged with the shared quiz-bundle properties the hpn-scripts-migration
  // discount Function groups by. Mirrors the same property names already
  // in production for the one-sol store's quiz — this is the SAME rule
  // type (quiz_bundle_price_match), just enabled for this shop's own
  // discount config, and entirely separate from this theme's existing
  // Bundle Builder feature's _bundle_item/_bundle_id properties.
  ProductQuiz.prototype.getBundleCartItems = function (item) {
    var qtyInput = item.querySelector('[data-quiz-qty-input]');
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 1;
    if (quantity <= 0) return null;

    var components = Array.prototype.slice.call(item.querySelectorAll('[data-component]'));
    var freebieEls = Array.prototype.slice.call(item.querySelectorAll('[data-freebie]'));

    // The default layout has no purchase-type toggle at all — its freebies
    // (when configured) stay unconditional, exactly as before this existed.
    // The new hero layout's toggle makes the real one-time/subscribe split
    // representable, so its freebies (Subscribe & Save exclusives on the
    // live site) only apply when subscribe is actually selected.
    var hasPurchaseToggle = !!item.querySelector('[data-quiz-purchase-option]');
    var isSubscribing = hasPurchaseToggle && this.getPurchaseType(item) === 'subscribe';
    var includeFreebies = !hasPurchaseToggle || isSubscribing;

    var bundleId = 'tq-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    var bundleName = item.getAttribute('data-result-title') || '';
    var bundleImageEl = item.querySelector('.quiz-hero__image, .product-quiz__bundle-item-image') ||
      (item.parentNode && item.parentNode.querySelector('.quiz-hero__image, .product-quiz__bundle-item-image'));
    var bundleImage = bundleImageEl ? (bundleImageEl.currentSrc || bundleImageEl.getAttribute('src') || '') : '';
    var targetCentsAttr = isSubscribing ? 'data-target-cents-subscribe' : 'data-target-cents';
    var targetCents = item.getAttribute(targetCentsAttr) || item.getAttribute('data-target-cents') || '';
    var expectedPaidCount = components.length;

    var paidProps = {
      _quiz_bundle_id: bundleId,
      _quiz_bundle_name: bundleName,
      _quiz_bundle_image: bundleImage,
      _quiz_target_cents: targetCents,
      _quiz_expected_paid_count: String(expectedPaidCount)
    };

    var items = [];
    var metaList = [];

    components.forEach(function (component) {
      var select = component.querySelector('[data-component-select]');
      var variantId, title, variantTitle;
      if (select) {
        variantId = parseInt(select.value, 10);
        var option = select.options[select.selectedIndex];
        title = component.getAttribute('data-component-title') || '';
        variantTitle = option ? option.textContent.trim() : '';
      } else {
        variantId = parseInt(component.getAttribute('data-variant-id'), 10);
        title = component.getAttribute('data-component-title') || '';
        variantTitle = '';
      }
      if (!variantId) return;
      var cartItem = { id: variantId, quantity: quantity, properties: paidProps };
      if (isSubscribing) {
        var sellingPlanId = parseInt(component.getAttribute('data-selling-plan-id'), 10);
        if (sellingPlanId) cartItem.selling_plan = sellingPlanId;
      }
      items.push(cartItem);
      metaList.push({ title: title, variantTitle: variantTitle });
    });

    if (includeFreebies) {
      freebieEls.forEach(function (freebieEl) {
        var freebieVariantId = parseInt(freebieEl.getAttribute('data-variant-id'), 10);
        if (!freebieVariantId) return;
        items.push({
          id: freebieVariantId,
          quantity: quantity,
          properties: {
            _quiz_bundle_id: bundleId,
            _quiz_bundle_name: bundleName,
            _quiz_bundle_image: bundleImage,
            _quiz_target_cents: targetCents,
            _quiz_expected_paid_count: String(expectedPaidCount),
            _quiz_free_gift: 'true'
          }
        });
        metaList.push({ title: freebieEl.getAttribute('data-component-title') || 'Free gift', variantTitle: '' });
      });
    }

    return items.length ? { items: items, metaList: metaList } : null;
  };

  // Single /cart/add.js call carrying every line at once. This is the fast
  // path postCartAdd tries first: Shopify's cart is a session-backed resource,
  // and firing one POST per item concurrently (the old approach) let two
  // requests read the same starting cart before either write landed, so the
  // second write silently clobbered the first — a real product would report
  // `ok: true` from its own request yet never end up in the cart. A single
  // request has nothing to race against.
  ProductQuiz.prototype.addCartItemsBatch = function (items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (response.ok) return { ok: true, data: data };
          return { ok: false, error: (data && (data.description || data.message)) || 'Out of stock' };
        });
      })
      .catch(function () {
        return { ok: false, kind: 'server_error', error: 'Something went wrong' };
      });
  };

  // Fallback for when the batch call above fails — Shopify's multi-item
  // /cart/add.js is all-or-nothing and doesn't say which line was the
  // problem, so this re-adds one item per request to find out. Chained
  // (not Promise.all) so these don't race each other the same way the old
  // all-parallel approach did.
  ProductQuiz.prototype.addCartItemsSequential = function (items, metaList) {
    var self = this;
    var outcomes = [];
    return items
      .reduce(function (chain, cartItem, i) {
        return chain.then(function () {
          var meta = (metaList && metaList[i]) || {};
          return self.addCartItem(cartItem, meta).then(function (outcome) {
            outcomes.push(outcome);
          });
        });
      }, Promise.resolve())
      .then(function () {
        return outcomes;
      });
  };

  // Only retries on `kind: 'server_error'` (fetch itself failing — offline,
  // timeout) — a real `kind: 'rejected'` response (out of stock) is a
  // definitive answer that retrying can never change.
  ProductQuiz.prototype.addCartItem = function (cartItem, meta, attempt) {
    var self = this;
    attempt = attempt || 1;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cartItem)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (response.ok) {
            return { ok: true, item: cartItem, meta: meta || {}, key: data.key, kind: 'ok' };
          }
          return {
            ok: false,
            item: cartItem,
            meta: meta || {},
            kind: 'rejected',
            error: (data && (data.description || data.message)) || 'Out of stock'
          };
        });
      })
      .catch(function () {
        if (attempt < CART_ADD_MAX_ATTEMPTS) {
          return new Promise(function (resolve) {
            window.setTimeout(function () {
              resolve(self.addCartItem(cartItem, meta, attempt + 1));
            }, CART_ADD_RETRY_DELAY_MS);
          });
        }
        return { ok: false, item: cartItem, meta: meta || {}, kind: 'server_error', error: 'Something went wrong' };
      });
  };

  // Best-effort rollback for a bundle add that partially succeeded — removes
  // lines this same click just added so a shopper never ends up with a
  // broken partial bundle (missing a paid component breaks the price-match
  // discount's own expectedPaidCount gate) sitting in their cart after a
  // stock/server failure on a sibling line.
  ProductQuiz.prototype.removeCartLine = function (key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).catch(function () {});
  };

  // Add-to-cart modal — loading state (spinner + rotating reassurance copy)
  // from click until the real outcome is known, result state only for
  // problems (out-of-stock is final; server error already retried a couple
  // of times by addCartItem). Skipped on full success — the existing status
  // text + cart drawer already cover that path.
  ProductQuiz.prototype.bindCartModal = function (resultEl) {
    var modal = resultEl.querySelector('[data-quiz-cart-modal]');
    if (!modal) return;
    var closeBtn = modal.querySelector('[data-quiz-cart-modal-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        modal.hidden = true;
      });
    }
  };

  ProductQuiz.prototype.stopCartModalRotation = function (resultEl) {
    var modal = resultEl.querySelector('[data-quiz-cart-modal]');
    if (modal && modal.__pqRotateTimer) {
      window.clearInterval(modal.__pqRotateTimer);
      modal.__pqRotateTimer = null;
    }
  };

  ProductQuiz.prototype.showCartModalLoading = function (resultEl) {
    var modal = resultEl.querySelector('[data-quiz-cart-modal]');
    if (!modal) return;
    var loadingEl = modal.querySelector('[data-quiz-cart-modal-loading]');
    var resultBox = modal.querySelector('[data-quiz-cart-modal-result]');
    var messageEl = modal.querySelector('[data-quiz-cart-modal-message]');

    this.stopCartModalRotation(resultEl);
    if (loadingEl) loadingEl.hidden = false;
    if (resultBox) resultBox.hidden = true;
    modal.hidden = false;

    if (messageEl) {
      var index = 0;
      messageEl.style.opacity = '1';
      messageEl.textContent = CART_WAITING_MESSAGES[0];
      modal.__pqRotateTimer = window.setInterval(function () {
        index = (index + 1) % CART_WAITING_MESSAGES.length;
        messageEl.style.opacity = '0';
        window.setTimeout(function () {
          messageEl.textContent = CART_WAITING_MESSAGES[index];
          messageEl.style.opacity = '1';
        }, 250);
      }, CART_WAITING_ROTATE_MS);
    }
  };

  ProductQuiz.prototype.showCartModalResult = function (resultEl, options) {
    this.stopCartModalRotation(resultEl);
    var modal = resultEl.querySelector('[data-quiz-cart-modal]');
    if (!modal) return;
    var loadingEl = modal.querySelector('[data-quiz-cart-modal-loading]');
    var resultBox = modal.querySelector('[data-quiz-cart-modal-result]');
    var iconEl = modal.querySelector('[data-quiz-cart-modal-icon]');
    var titleEl = modal.querySelector('[data-quiz-cart-modal-title]');
    var detailEl = modal.querySelector('[data-quiz-cart-modal-detail]');

    if (loadingEl) loadingEl.hidden = true;
    if (iconEl) iconEl.textContent = options.icon || '';
    if (titleEl) titleEl.textContent = options.title || '';
    if (detailEl) detailEl.textContent = options.detail || '';
    if (resultBox) resultBox.hidden = false;
    modal.hidden = false;
  };

  ProductQuiz.prototype.hideCartModal = function (resultEl) {
    this.stopCartModalRotation(resultEl);
    var modal = resultEl.querySelector('[data-quiz-cart-modal]');
    if (modal) modal.hidden = true;
  };

  ProductQuiz.prototype.showQuizBundleSkeleton = function (bundleId, itemCount) {
    var anchor = document.getElementById('quiz-group-card-anchor');
    if (!anchor || anchor.querySelector('[data-quiz-bundle-summary="' + bundleId + '"]')) return;

    var slide = '<div class="quiz-group-card__slide">' +
      '<div class="quiz-group-card__slide-img-wrap"></div>' +
      '<span class="quiz-group-card__slide-name"></span>' +
    '</div>';

    var card = document.createElement('div');
    card.className = 'quiz-group-card quiz-group-card--loading';
    card.setAttribute('data-quiz-bundle-summary', bundleId);
    card.innerHTML =
      '<div class="quiz-group-card__header">' +
        '<span class="quiz-group-card__badge">Quiz Bundle</span>' +
        '<span class="quiz-group-card__title" data-quiz-title></span>' +
        '<button type="button" class="quiz-group-card__remove-btn" aria-label="Remove bundle" disabled>' +
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="quiz-group-card__carousel" data-quiz-carousel>' + new Array(Math.max(itemCount, 1) + 1).join(slide) + '</div>' +
      '<div class="quiz-group-card__footer">' +
        '<p class="quiz-group-card__meta" data-quiz-meta></p>' +
        '<span class="quiz-group-card__total" data-quiz-total></span>' +
      '</div>';
    anchor.appendChild(card);
  };

  ProductQuiz.prototype.openCartDrawer = function () {
    var drawer = document.querySelector('[data-drawer="drawer-cart"]');
    fetch('/cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
        if (drawer) drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: true }));
      })
      .catch(function () {});
  };


  ProductQuiz.prototype.postCartAdd = function (resultEl, items, statusEl, button, metaList) {
    var self = this;
    var setStatus = function (text, isError) {
      if (!statusEl) return;
      statusEl.textContent = text;
      statusEl.hidden = !text;
      statusEl.classList.toggle('product-quiz__order-summary-status--error', !!isError);
      statusEl.classList.toggle('quiz-hero__status--error', !!isError);
    };

    if (!items.length) {
      setStatus('Select your bundle first.', true);
      return;
    }

    if (button) button.disabled = true;
    setStatus('Adding to cart…');
    if (resultEl) self.showCartModalLoading(resultEl);

    var handleOutcomes = function (outcomes) {
      var succeeded = outcomes.filter(function (o) { return o.ok; });
      var failed = outcomes.filter(function (o) { return !o.ok; });

      if (!failed.length) {
        if (button) button.disabled = false;
        if (resultEl) self.hideCartModal(resultEl);
        setStatus('Added to cart!');

        var bundleId = succeeded.length && succeeded[0].item.properties
          ? succeeded[0].item.properties._quiz_bundle_id
          : null;
        if (bundleId) self.showQuizBundleSkeleton(bundleId, succeeded.length);
        self.openCartDrawer();

        var enrichedItems = succeeded.map(function (o) {
          return {
            id: o.item.id,
            quantity: o.item.quantity,
            title: o.meta.title || '',
            variantTitle: o.meta.variantTitle || ''
          };
        });
        document.dispatchEvent(new CustomEvent('product-quiz:added-to-cart', {
          detail: { items: enrichedItems },
          bubbles: true
        }));
        return;
      }

      // Something in the bundle failed — roll back whatever DID succeed so
      // the cart never holds a broken partial bundle, then explain why.
      var rollbacks = succeeded
        .filter(function (o) { return o.key; })
        .map(function (o) { return self.removeCartLine(o.key); });

      Promise.all(rollbacks).then(function () {
        if (button) button.disabled = false;

        var stockFailures = failed.filter(function (o) { return o.kind === 'rejected'; });

        if (stockFailures.length) {
          var names = stockFailures.map(function (o) { return o.meta.title || 'An item'; }).join(', ');
          var verb = stockFailures.length > 1 ? 'are' : 'is';
          setStatus(names + ' — out of stock.', true);
          if (resultEl) {
            self.showCartModalResult(resultEl, {
              icon: '📦',
              title: 'Out of stock',
              detail: names + ' ' + verb + ' out of stock. Nothing was added — check back soon.'
            });
            // Reflects the real-time rejection immediately, instead of only
            // on the next page load — keeps syncStockAvailability's own
            // disable-the-button logic correct right away.
            var item = resultEl.querySelector('[data-quiz-item]');
            stockFailures.forEach(function (o) {
              var el = item && item.querySelector('[data-variant-id="' + o.item.id + '"]');
              if (el) el.setAttribute('data-available', 'false');
            });
            self.syncStockAvailability(resultEl);
          }
        } else {
          setStatus('Something went wrong — please try again.', true);
          if (resultEl) {
            self.showCartModalResult(resultEl, {
              icon: '⚠️',
              title: 'Something went wrong',
              detail: 'Nothing was added. Please try again.'
            });
          }
        }
      });
    };

    // Fast path: one request for the whole bundle. Only when that fails
    // (all-or-nothing, so it doesn't say which line) do we fall back to
    // one request per item — sequentially — to find the culprit and roll
    // back whatever else went through.
    self.addCartItemsBatch(items).then(function (batchResult) {
      if (batchResult.ok) {
        handleOutcomes(items.map(function (cartItem, i) {
          return { ok: true, item: cartItem, meta: (metaList && metaList[i]) || {} };
        }));
        return;
      }
      self.addCartItemsSequential(items, metaList).then(handleOutcomes);
    });
  };

  ProductQuiz.prototype.addBundleToCart = function (item, button, statusElOverride) {
    var statusEl = statusElOverride || item.querySelector('[data-quiz-item-status]');
    var resultEl = item.closest('[data-quiz-result]');
    var bundleCart = this.getBundleCartItems(item);
    if (!bundleCart) {
      if (statusEl) {
        statusEl.textContent = 'Select your bundle first.';
        statusEl.hidden = false;
      }
      return;
    }
    this.postCartAdd(resultEl, bundleCart.items, statusEl, button, bundleCart.metaList);
  };

  ProductQuiz.prototype.retake = function () {
    var self = this;
    this.answers = {};
    this.selectedBranch = null;
    this.activeSteps = [];
    this.index = 0;
    this.clearStorage();

    this.steps.forEach(function (step) {
      step.querySelectorAll('[data-quiz-option]').forEach(function (option) {
        option.setAttribute('aria-checked', 'false');
      });
      self.updateNextButtonState(step);
    });

    this.results.forEach(function (resultEl) {
      resultEl.hidden = true;

      var selectRow = resultEl.querySelector('[data-quiz-select-row]');
      if (selectRow) {
        var featuredItem = selectRow.closest('[data-quiz-item]');
        var qtyInput = featuredItem ? featuredItem.querySelector('[data-quiz-qty-input]') : null;
        if (qtyInput && (parseInt(qtyInput.value, 10) || 0) <= 0) {
          qtyInput.value = 1;
        }
        if (featuredItem) self.syncFeaturedSelection(featuredItem);
      }
    });
    if (this.bundleLink) this.bundleLink.hidden = true;
    if (this.resultsPanel) this.resultsPanel.hidden = true;
    if (this.stepsContainer) this.stepsContainer.hidden = false;

    if (this.intro) {
      this.intro.hidden = false;
      if (this.progress) this.progress.hidden = true;
    } else if (this.progress) {
      this.progress.hidden = !this.progressEnabled;
    }
  };

  ProductQuiz.prototype.readStorage = function () {
    try {
      var raw = window.localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  ProductQuiz.prototype.persistState = function (resultTag, completed) {
    if (!this.persist) return;
    try {
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          version: STATE_VERSION,
          index: this.index,
          answers: this.answers,
          selectedBranch: this.selectedBranch,
          email: this.email,
          completed: !!completed,
          resultTag: resultTag || null
        })
      );
    } catch (e) {}
  };

  ProductQuiz.prototype.clearStorage = function () {
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (e) {}
  };

  // Debounced re-measure of every step's natural content height (including hidden ones —
  // temporarily unhidden off-screen to measure, then restored) so all steps can share one
  // fixed min-height (--pq-step-min-height, see component-product-quiz.css). Without it the
  // card visibly resizes on every Next/Back, which reads as jumpy against a static background
  // image or a shared "position: absolute" background layer.
  ProductQuiz.prototype.bindResize = function () {
    var self = this;
    var timer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        self.equalizeStepHeights();
      }, 150);
    });
  };

  ProductQuiz.prototype.equalizeStepHeights = function () {
    if (!this.steps.length) return;
    var max = 0;

    this.steps.forEach(function (step) {
      var content = step.querySelector('.product-quiz__step-content');
      if (!content) return;

      var wasHidden = step.hidden;
      var prevPosition = step.style.position;
      var prevVisibility = step.style.visibility;
      var prevMinHeight = content.style.minHeight;

      step.hidden = false;
      step.style.position = 'absolute';
      step.style.visibility = 'hidden';
      content.style.minHeight = '0px';

      if (content.offsetHeight > max) max = content.offsetHeight;

      step.hidden = wasHidden;
      step.style.position = prevPosition;
      step.style.visibility = prevVisibility;
      content.style.minHeight = prevMinHeight;
    });

    if (max > 0) {
      this.root.style.setProperty('--pq-step-min-height', max + 'px');
    }
  };

  function init() {
    var roots = document.querySelectorAll('[data-product-quiz]');
    roots.forEach(function (root) {
      if (root.__productQuizInitialized) return;
      root.__productQuizInitialized = true;
      new ProductQuiz(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector && event.target.querySelector('[data-product-quiz]');
    if (root && !root.__productQuizInitialized) {
      root.__productQuizInitialized = true;
      new ProductQuiz(root);
    }
  });
})();
